import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Plus,
  Trash2,
  History,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Users,
  User,
  Loader2,
  ChevronDown,
  Check,
  ChevronsUpDown,
  ChevronUp,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { aiModuleAPI } from "@/services/api";
import { cn } from "@/lib/utils";

const ManualQuestionModal = ({
  isOpen,
  onClose,
  eligibleCandidates = [],
  aiModuleId,
  onQuestionsAdded,
  questionGenerationChoice = null, // Add this prop to handle existing/new choice
}) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("create");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Custom accordion state for question management
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(0); // Currently editing question
  
  // Question creation state
  const [questions, setQuestions] = useState([{
    id: Date.now(),
    question: "",
    options: ["", "", "", ""],
    correctAnswer: "",
    difficulty: "medium",
    category: "General",
    subCategory: "",
    tags: [],
    newTag: "",
    assignedCandidates: [], // Individual assignment for each question
  }]);
  
  // Remove global candidate assignment state since we're doing per-question assignment
  // const [selectedCandidates, setSelectedCandidates] = useState([]);
  // const [assignToAll, setAssignToAll] = useState(true);
  
  // History state
  const [historyQuestions, setHistoryQuestions] = useState([]);
  const [selectedHistoryQuestions, setSelectedHistoryQuestions] = useState([]);
  const [historySearchTerm, setHistorySearchTerm] = useState("");
  
  // Pagination state for history
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [questionsPerPage] = useState(10); // Compact pagination with 10 questions per page
  
  // Global candidate assignment for history (since history questions are assigned in bulk)
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState({});

  // Multi-select dropdown state for individual questions
  const [questionDropdownOpen, setQuestionDropdownOpen] = useState({});
  const [questionSearchTerms, setQuestionSearchTerms] = useState({});
  
  // Multi-select dropdown state for history tab
  const [historyDropdownOpen, setHistoryDropdownOpen] = useState(false);
  const [historyCandidateSearchTerm, setHistoryCandidateSearchTerm] = useState("");

  // Initialize candidate selection for history only
  useEffect(() => {
    if (eligibleCandidates.length === 1) {
      setSelectedCandidates([eligibleCandidates[0]._id]);
    } else if (eligibleCandidates.length > 1) {
      setSelectedCandidates(eligibleCandidates.map(c => c._id));
    }
  }, [eligibleCandidates]);

  // Load history when tab changes
  useEffect(() => {
    if (activeTab === "history") {
      setCurrentPage(1); // Reset to first page when switching to history tab
      loadQuestionHistory(1);
    }
  }, [activeTab]);

  // Force refresh history when modal opens
  useEffect(() => {
    if (isOpen && activeTab === "history") {
      setCurrentPage(1);
      loadQuestionHistory(1);
    }
  }, [isOpen]);

  // Load history when search term changes (with debounce)
  useEffect(() => {
    if (activeTab === "history") {
      const timeoutId = setTimeout(() => {
        setCurrentPage(1);
        loadQuestionHistory(1, historySearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [historySearchTerm, activeTab]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      loadQuestionHistory(newPage, historySearchTerm);
    }
  };

  const loadQuestionHistory = async (page = 1, search = "") => {
    setHistoryLoading(true);
    try {
      const response = await aiModuleAPI.getManualQuestionHistory({
        page,
        limit: questionsPerPage,
        search: search.trim()
      });
      
      if (response.data.success) {
        const historyData = response.data.data || [];
        const pagination = response.data.pagination || {};
        
        
        setHistoryQuestions(historyData);
        setCurrentPage(pagination.currentPage || 1);
        setTotalPages(pagination.totalPages || 1);
        setTotalQuestions(pagination.total || 0);
        
        // Clear selected questions when loading new page/search
        setSelectedHistoryQuestions([]);
        
        // Force a small delay to ensure state update
        setTimeout(() => {
          console.log("📚 Debug - History questions state updated");
        }, 100);
      }
    } catch (error) {
      console.error("Error loading question history:", error);
      toast({
        title: "Error",
        description: "Failed to load question history",
        variant: "destructive",
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  const validateQuestion = (question, index) => {
    const errors = {};
    
    if (!question.question || !question.question.trim()) {
      errors.question = "Question text is required";
    }
    
    const filledOptions = question.options.filter(opt => opt && opt.trim());
    if (filledOptions.length < 2) {
      errors.options = "At least 2 options are required";
    }
    
    if (!question.correctAnswer) {
      errors.correctAnswer = "Correct answer must be selected";
    } else if (!filledOptions.includes(question.correctAnswer)) {
      errors.correctAnswer = "Correct answer must be one of the provided options";
    }
    
    if (!question.difficulty) {
      errors.difficulty = "Difficulty level is required";
    }
    
    return errors;
  };

  const validateAllQuestions = () => {
    const allErrors = {};
    let hasErrors = false;
    
    questions.forEach((question, index) => {
      const errors = validateQuestion(question, index);
      if (Object.keys(errors).length > 0) {
        allErrors[index] = errors;
        hasErrors = true;
      }
    });
    
    setValidationErrors(allErrors);
    return !hasErrors;
  };

  const addNewQuestion = () => {
    const newQuestionIndex = questions.length;
    
    setQuestions([...questions, {
      id: Date.now(),
      question: "",
      options: ["", "", "", ""],
      correctAnswer: "",
      difficulty: "medium",
      category: "General",
      subCategory: "",
      tags: [],
      newTag: "",
      assignedCandidates: [], // Each new question starts with no assignments
    }]);
    
    // Set the new question as the editing question
    setEditingQuestionIndex(newQuestionIndex);
  };

  const removeQuestion = (index) => {
    if (questions.length > 1) {
      const newQuestions = questions.filter((_, i) => i !== index);
      setQuestions(newQuestions);
      
      // Clear validation errors for removed question
      const newErrors = { ...validationErrors };
      delete newErrors[index];
      setValidationErrors(newErrors);
      
      // If we removed the currently editing question, set editing to first question
      if (editingQuestionIndex === index) {
        setEditingQuestionIndex(0);
      } else if (editingQuestionIndex > index) {
        setEditingQuestionIndex(editingQuestionIndex - 1);
      }
    }
  };

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setQuestions(newQuestions);
    
    // Clear validation error for this field
    if (validationErrors[index]?.[field]) {
      const newErrors = { ...validationErrors };
      delete newErrors[index][field];
      setValidationErrors(newErrors);
    }
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const newQuestions = [...questions];
    newQuestions[questionIndex].options[optionIndex] = value;
    setQuestions(newQuestions);
  };

  const addOption = (questionIndex) => {
    const newQuestions = [...questions];
    if (newQuestions[questionIndex].options.length < 6) {
      newQuestions[questionIndex].options.push("");
      setQuestions(newQuestions);
    }
  };

  const removeOption = (questionIndex, optionIndex) => {
    const newQuestions = [...questions];
    if (newQuestions[questionIndex].options.length > 2) {
      newQuestions[questionIndex].options.splice(optionIndex, 1);
      // Reset correct answer if it was the removed option
      if (newQuestions[questionIndex].correctAnswer === newQuestions[questionIndex].options[optionIndex]) {
        newQuestions[questionIndex].correctAnswer = "";
      }
      setQuestions(newQuestions);
    }
  };

  const addTag = (questionIndex) => {
    const question = questions[questionIndex];
    if (question.newTag.trim() && !question.tags.includes(question.newTag.trim())) {
      updateQuestion(questionIndex, 'tags', [...question.tags, question.newTag.trim()]);
      updateQuestion(questionIndex, 'newTag', '');
    }
  };

  const removeTag = (questionIndex, tagIndex) => {
    const newQuestions = [...questions];
    newQuestions[questionIndex].tags.splice(tagIndex, 1);
    setQuestions(newQuestions);
  };

  // Individual question assignment functions
  const assignAllCandidatesToQuestion = (questionIndex) => {
    const newQuestions = [...questions];
    newQuestions[questionIndex].assignedCandidates = eligibleCandidates.map(c => c._id);
    setQuestions(newQuestions);
  };

  const clearQuestionAssignments = (questionIndex) => {
    const newQuestions = [...questions];
    newQuestions[questionIndex].assignedCandidates = [];
    setQuestions(newQuestions);
  };

  const submitQuestions = async () => {
    
    if (!validateAllQuestions()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in your questions",
        variant: "destructive",
      });
      return;
    }

    // Check if at least one question has candidates assigned
    const questionsWithAssignments = questions.filter(q => q.assignedCandidates && q.assignedCandidates.length > 0);
    if (questionsWithAssignments.length === 0) {
      toast({
        title: "No Assignments",
        description: "Please assign at least one question to candidates",
        variant: "destructive",
      });
      return;
    }


    setLoading(true);
    try {
      // Group questions by candidate for efficient API calls
      const candidateQuestionMap = new Map();
      
      
      questions.forEach((q, index) => {
        if (q.assignedCandidates && q.assignedCandidates.length > 0) {
          // Filter out empty options
          const filteredOptions = q.options.filter(opt => opt && opt.trim());
          
          // Enhanced debug logging
          
          // Validate that correctAnswer is in filtered options
          if (!q.correctAnswer || !filteredOptions.includes(q.correctAnswer)) {
            throw new Error(`Question ${index + 1}: Correct answer "${q.correctAnswer}" is not in the filtered options: ${filteredOptions.join(', ')}`);
          }
          
          // Validate minimum options
          if (filteredOptions.length < 2) {
            throw new Error(`Question ${index + 1}: At least 2 non-empty options are required. Found: ${filteredOptions.length}`);
          }
          
          // Find the correct answer index
          const correctAnswerIndex = filteredOptions.indexOf(q.correctAnswer);
          if (correctAnswerIndex === -1) {
            throw new Error(`Question ${index + 1}: Correct answer "${q.correctAnswer}" not found in options`);
          }

          const questionData = {
            question: q.question.trim(),
            options: filteredOptions,
            correctAnswerIndex: correctAnswerIndex,
            difficulty: q.difficulty.charAt(0).toUpperCase() + q.difficulty.slice(1).toLowerCase(),
            category: q.category || "General",
            subCategory: q.subCategory || "",
            tags: q.tags || [],
          };
          
          
          q.assignedCandidates.forEach(candidateId => {
            if (!candidateQuestionMap.has(candidateId)) {
              candidateQuestionMap.set(candidateId, []);
            }
            candidateQuestionMap.get(candidateId).push(questionData);
          });
        }
      });

      // Submit questions for each candidate individually to respect per-question assignments
      let totalQuestionsAdded = 0;
      let totalCandidatesUpdated = 0;
      const allResponses = [];
      
      
      // Submit questions for each candidate separately to respect individual assignments
      for (const [candidateId, candidateQuestions] of candidateQuestionMap.entries()) {
        const candidateName = eligibleCandidates.find(c => c._id === candidateId)?.name || 'Unknown';
        
        
        const response = await aiModuleAPI.addManualQuestions({
          aiModuleId,
          questions: candidateQuestions,
          candidateIds: [candidateId], // Only this specific candidate
          questionGenerationChoice: questionGenerationChoice, // Pass the choice to backend
        });


        if (response.data.success) {
          totalQuestionsAdded += candidateQuestions.length;
          totalCandidatesUpdated += 1;
          allResponses.push(response.data);
        } else {
          throw new Error(`Failed to assign questions to ${candidateName}: ${response.data?.message || 'Unknown error'}`);
        }
      }
      
      // Create a combined response object
      const response = {
        data: {
          success: true,
          message: `Successfully assigned questions to ${totalCandidatesUpdated} candidate(s)`,
          data: {
            questionsAdded: totalQuestionsAdded,
            candidatesUpdated: Array.from(candidateQuestionMap.keys()).map(candidateId => ({
              candidateId,
              candidateName: eligibleCandidates.find(c => c._id === candidateId)?.name || 'Unknown',
              questionsAdded: candidateQuestionMap.get(candidateId).length
            })),
            totalQuestionsAdded,
            candidateCount: totalCandidatesUpdated,
            allResponses
          }
        }
      };


      toast({
        title: "Success",
        description: `${totalQuestionsAdded} question(s) assigned to ${totalCandidatesUpdated} candidate(s)`,
      });
      
      onQuestionsAdded?.(response.data.data);
      
      onClose();

    } catch (error) {
      console.error("❌ Frontend Debug - Error adding manual questions:", error);
      console.error("❌ Frontend Debug - Error response:", error.response?.data);
      console.error("❌ Frontend Debug - Error status:", error.response?.status);
      console.error("❌ Frontend Debug - Error message:", error.message);
      
      // Show more detailed error message
      let errorMessage = "Failed to add questions";
      if (error.response?.data?.userFriendlyMessage) {
        errorMessage = error.response.data.userFriendlyMessage;
        console.error("❌ Frontend Debug - Using user-friendly error message:", errorMessage);
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        console.error("❌ Frontend Debug - Using backend error message:", errorMessage);
      } else if (error.message) {
        errorMessage = error.message;
        console.error("❌ Frontend Debug - Using generic error message:", errorMessage);
      }
      
      // Check if questions were actually saved despite the error
      if (error.response?.data?.debug?.questionsActuallySaved > 0) {
        console.warn("⚠️ Frontend Debug - Questions were saved despite error:", {
          savedCount: error.response.data.debug.questionsActuallySaved,
          savedIds: error.response.data.debug.savedQuestionIds
        });
        
        errorMessage += ` (Note: ${error.response.data.debug.questionsActuallySaved} question(s) were actually saved to database)`;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const assignHistoryQuestions = async () => {
    if (selectedHistoryQuestions.length === 0) {
      toast({
        title: "No Questions Selected",
        description: "Please select questions from history",
        variant: "destructive",
      });
      return;
    }

    if (selectedCandidates.length === 0) {
      toast({
        title: "No Candidates Selected",
        description: "Please select at least one candidate",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      
      const response = await aiModuleAPI.assignHistoryQuestions({
        aiModuleId,
        questionIds: selectedHistoryQuestions,
        candidateIds: selectedCandidates,
        questionGenerationChoice: questionGenerationChoice, // Pass the choice to backend
      });

      if (response.data.success) {
        
        const { totalQuestionsAdded, duplicatesSkipped, duplicateDetails, hasDuplicates } = response.data.data;
        
        // Handle different scenarios based on results
        if (hasDuplicates && totalQuestionsAdded === 0) {
          // All questions were duplicates
          const duplicateMessages = duplicateDetails.map(detail => 
            `${detail.candidateName}: ${detail.duplicatesSkipped} question(s) already assigned`
          ).join('\n');
          
          toast({
            title: "Questions Already Assigned",
            description: `All selected questions are already assigned to the selected candidates:\n${duplicateMessages}`,
            variant: "destructive",
          });
        } else if (hasDuplicates && totalQuestionsAdded > 0) {
          // Some questions were duplicates, some were assigned
          const duplicateMessages = duplicateDetails.map(detail => 
            `${detail.candidateName}: ${detail.duplicatesSkipped} question(s) already assigned`
          ).join('\n');
          
          toast({
            title: "Partial Assignment",
            description: `${totalQuestionsAdded} question(s) assigned successfully. ${duplicatesSkipped} question(s) were already assigned and skipped:\n${duplicateMessages}`,
          });
        } else {
          // All questions assigned successfully
          toast({
            title: "Success",
            description: `${totalQuestionsAdded} question(s) assigned successfully`,
          });
        }
        
        // Call the callback with the response data
        onQuestionsAdded?.(response.data.data);
        
        // Close modal after successful assignment (even if some were duplicates)
        onClose();
      } else {
        throw new Error(response.data.message || "Failed to assign questions");
      }
    } catch (error) {
      console.error("Error assigning history questions:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to assign questions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setQuestions([{
      id: Date.now(),
      question: "",
      options: ["", "", "", ""],
      correctAnswer: "",
      difficulty: "medium",
      category: "General",
      subCategory: "",
      tags: [],
      newTag: "",
      assignedCandidates: [],
    }]);
    setSelectedCandidates([]);
    setSelectedHistoryQuestions([]);
    setValidationErrors({});
    setActiveTab("create");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-6xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
        {/* Fixed Header */}
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b bg-white">
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-blue-600" />
            Add Questions Manually
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          {/* Fixed Header - Tabs */}
          <div className="flex-shrink-0 px-6 py-3 border-b bg-gray-50">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="create" className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Create New
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-2">
                <History className="h-4 w-4" />
                Question History
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="create" className="flex-1 overflow-y-auto min-h-0 mx-0 mt-0">
            <div className="px-6 pt-4 pb-2 space-y-4">
              {questions.map((question, questionIndex) => {
                  const isComplete = question.question.trim() && 
                                   question.options.filter(opt => opt.trim()).length >= 2 && 
                                   question.correctAnswer && 
                                   question.assignedCandidates?.length > 0;
                  const isExpanded = editingQuestionIndex === questionIndex;
                  
                  return (
                    <div key={question.id} className="border border-gray-200 rounded-lg bg-white shadow-sm">
                      {/* Question Header - Always Visible */}
                      <div 
                        className="px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100"
                        onClick={() => {
                          setEditingQuestionIndex(isExpanded ? -1 : questionIndex);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-gray-900">Question {questionIndex + 1}</span>
                            {isComplete ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <AlertCircle className="h-4 w-4 text-orange-500" />
                            )}
                            {question.question.trim() ? (
                              <span className="text-sm text-gray-600 truncate max-w-xs">
                                {question.question.substring(0, 50)}...
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400 italic">Click to add question</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {question.assignedCandidates?.length > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {question.assignedCandidates.length} assigned
                              </Badge>
                            )}
                            {questions.length > 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeQuestion(questionIndex);
                                }}
                                className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                      </div>

                      {/* Question Content - Expandable */}
                      {isExpanded && (
                        <div className="px-4 py-4">
                          <div className="space-y-4">
                            {/* Question Text */}
                            <div className="space-y-2">
                              <Label htmlFor={`question-${questionIndex}`}>
                                Question Text <span className="text-red-500">*</span>
                              </Label>
                              <Textarea
                                id={`question-${questionIndex}`}
                                placeholder="Enter your question here..."
                                value={question.question}
                                onChange={(e) => updateQuestion(questionIndex, 'question', e.target.value)}
                                className={validationErrors[questionIndex]?.question ? "border-red-500" : ""}
                                rows={3}
                              />
                              {validationErrors[questionIndex]?.question && (
                                <p className="text-sm text-red-600 flex items-center gap-1">
                                  <AlertCircle className="h-4 w-4" />
                                  {validationErrors[questionIndex].question}
                                </p>
                              )}
                            </div>

                            {/* Options */}
                            <div className="space-y-2">
                              <Label>
                                Answer Options <span className="text-red-500">*</span>
                              </Label>
                              <div className="space-y-2">
                                {question.options.map((option, optionIndex) => (
                                  <div key={optionIndex} className="flex items-center gap-2">
                                    <span className="text-sm font-medium w-8 text-gray-600">
                                      {String.fromCharCode(65 + optionIndex)}.
                                    </span>
                                    <Input
                                      placeholder={`Option ${String.fromCharCode(65 + optionIndex)}`}
                                      value={option}
                                      onChange={(e) => updateOption(questionIndex, optionIndex, e.target.value)}
                                      className="flex-1"
                                    />
                                    {question.options.length > 2 && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeOption(questionIndex, optionIndex)}
                                        className="text-red-600 hover:text-red-700"
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                ))}
                                {question.options.length < 6 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => addOption(questionIndex)}
                                    className="mt-2"
                                  >
                                    <Plus className="h-4 w-4 mr-1" />
                                    Add Option
                                  </Button>
                                )}
                              </div>
                              {validationErrors[questionIndex]?.options && (
                                <p className="text-sm text-red-600 flex items-center gap-1">
                                  <AlertCircle className="h-4 w-4" />
                                  {validationErrors[questionIndex].options}
                                </p>
                              )}
                            </div>

                            {/* Correct Answer */}
                            <div className="space-y-2">
                              <Label>
                                Correct Answer <span className="text-red-500">*</span>
                              </Label>
                              <Select
                                value={question.correctAnswer}
                                onValueChange={(value) => updateQuestion(questionIndex, 'correctAnswer', value)}
                              >
                                <SelectTrigger className={validationErrors[questionIndex]?.correctAnswer ? "border-red-500" : ""}>
                                  <SelectValue placeholder="Select correct answer" />
                                </SelectTrigger>
                                <SelectContent>
                                  {question.options
                                    .filter(opt => opt.trim())
                                    .map((option, optionIndex) => (
                                      <SelectItem key={optionIndex} value={option}>
                                        {String.fromCharCode(65 + optionIndex)}. {option}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              {validationErrors[questionIndex]?.correctAnswer && (
                                <p className="text-sm text-red-600 flex items-center gap-1">
                                  <AlertCircle className="h-4 w-4" />
                                  {validationErrors[questionIndex].correctAnswer}
                                </p>
                              )}
                            </div>

                            {/* Question Metadata */}
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>
                                  Difficulty Level <span className="text-red-500">*</span>
                                </Label>
                                <Select
                                  value={question.difficulty}
                                  onValueChange={(value) => updateQuestion(questionIndex, 'difficulty', value)}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="easy">Easy</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="hard">Hard</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              <div className="space-y-2">
                                <Label>Category</Label>
                                <Input
                                  placeholder="e.g., JavaScript, React, etc."
                                  value={question.category}
                                  onChange={(e) => updateQuestion(questionIndex, 'category', e.target.value)}
                                />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label>Sub Category (Optional)</Label>
                              <Input
                                placeholder="e.g., Functions, Components, etc."
                                value={question.subCategory}
                                onChange={(e) => updateQuestion(questionIndex, 'subCategory', e.target.value)}
                              />
                            </div>

                            {/* Tags */}
                            <div className="space-y-2">
                              <Label>Tags (Optional)</Label>
                              <div className="flex flex-wrap gap-2 mb-2">
                                {question.tags.map((tag, tagIndex) => (
                                  <Badge key={tagIndex} variant="secondary" className="flex items-center gap-1">
                                    {tag}
                                    <X
                                      className="h-3 w-3 cursor-pointer"
                                      onClick={() => removeTag(questionIndex, tagIndex)}
                                    />
                                  </Badge>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <Input
                                  placeholder="Add a tag..."
                                  value={question.newTag}
                                  onChange={(e) => updateQuestion(questionIndex, 'newTag', e.target.value)}
                                  onKeyPress={(e) => e.key === 'Enter' && addTag(questionIndex)}
                                />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => addTag(questionIndex)}
                                  disabled={!question.newTag.trim()}
                                >
                                  Add
                                </Button>
                              </div>
                            </div>

                            {/* Individual Question Assignment */}
                            <div className="space-y-3 border-t pt-4">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Assign This Question To:</Label>
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => assignAllCandidatesToQuestion(questionIndex)}
                                    disabled={eligibleCandidates.length === 0}
                                  >
                                    <Users className="h-3 w-3 mr-1" />
                                    All
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => clearQuestionAssignments(questionIndex)}
                                    disabled={!question.assignedCandidates || question.assignedCandidates.length === 0}
                                  >
                                    <X className="h-3 w-3 mr-1" />
                                    Clear
                                  </Button>
                                </div>
                              </div>
                              
                              {eligibleCandidates.length === 0 ? (
                                <p className="text-sm text-gray-500 italic">No eligible candidates available</p>
                              ) : (
                                <div className="space-y-2">
                                  <Popover 
                                    open={questionDropdownOpen[questionIndex] || false} 
                                    onOpenChange={(open) => setQuestionDropdownOpen(prev => ({...prev, [questionIndex]: open}))}
                                  >
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={questionDropdownOpen[questionIndex] || false}
                                        className="w-full justify-between min-h-10 h-auto py-2"
                                      >
                                        <div className="flex flex-wrap gap-1 max-w-[calc(100%-40px)] items-start">
                                          {(!question.assignedCandidates || question.assignedCandidates.length === 0) ? (
                                            <span className="text-muted-foreground py-1">Select candidates for this question...</span>
                                          ) : (
                                            <span className="py-1 text-left">
                                              {question.assignedCandidates.length === 1 
                                                ? eligibleCandidates.find(c => c._id === question.assignedCandidates[0])?.name
                                                : `${question.assignedCandidates.length} candidates selected`
                                              }
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-start gap-1 shrink-0 ml-2 mt-1">
                                          {question.assignedCandidates && question.assignedCandidates.length > 0 && (
                                            <X 
                                              className="h-4 w-4 opacity-50 hover:opacity-100" 
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                clearQuestionAssignments(questionIndex);
                                              }}
                                            />
                                          )}
                                          <ChevronsUpDown className="h-4 w-4 opacity-50" />
                                        </div>
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent 
                                      className="w-full p-0" 
                                      style={{ width: "max(var(--radix-popover-trigger-width), 320px)" }}
                                      side="bottom"
                                      align="start"
                                      sideOffset={4}
                                    >
                                      <div className="relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
                                        <div className="max-h-80 overflow-y-auto scrollbar-hidden">
                                          <div className="p-1">
                                            {/* Search Input */}
                                            <div className="p-2 border-b mb-1 sticky top-0 bg-popover z-10">
                                              <Input
                                                placeholder="Search candidates..."
                                                value={questionSearchTerms[questionIndex] || ""}
                                                onChange={(e) => setQuestionSearchTerms(prev => ({...prev, [questionIndex]: e.target.value}))}
                                                className="h-8"
                                              />
                                            </div>

                                            {/* Individual Candidates */}
                                            {eligibleCandidates
                                              .filter(candidate => {
                                                const searchTerm = questionSearchTerms[questionIndex] || "";
                                                if (!searchTerm) return true;
                                                return candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                       (candidate.email && candidate.email.toLowerCase().includes(searchTerm.toLowerCase()));
                                              })
                                              .map((candidate) => (
                                                <div
                                                  key={candidate._id}
                                                  onClick={() => {
                                                    const newQuestions = [...questions];
                                                    const assignedCandidates = newQuestions[questionIndex].assignedCandidates || [];
                                                    
                                                    if (assignedCandidates.includes(candidate._id)) {
                                                      newQuestions[questionIndex].assignedCandidates = assignedCandidates.filter(id => id !== candidate._id);
                                                    } else {
                                                      newQuestions[questionIndex].assignedCandidates = [...assignedCandidates, candidate._id];
                                                    }
                                                    
                                                    setQuestions(newQuestions);
                                                  }}
                                                  className="relative flex w-full cursor-default select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground cursor-pointer"
                                                >
                                                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                                    <Check
                                                      className={cn(
                                                        "h-4 w-4",
                                                        (question.assignedCandidates || []).includes(candidate._id) ? "opacity-100" : "opacity-0"
                                                      )}
                                                    />
                                                  </span>
                                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                                    <User className="h-4 w-4 flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                      <div className="font-medium truncate" title={candidate.name}>
                                                        {candidate.name}
                                                      </div>
                                                      {candidate.email && (
                                                        <div className="text-xs text-muted-foreground truncate" title={candidate.email}>
                                                          {candidate.email}
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                              ))}

                                            {/* No Results */}
                                            {eligibleCandidates.filter(candidate => {
                                              const searchTerm = questionSearchTerms[questionIndex] || "";
                                              if (!searchTerm) return true;
                                              return candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                     (candidate.email && candidate.email.toLowerCase().includes(searchTerm.toLowerCase()));
                                            }).length === 0 && (
                                              <div className="p-4 text-center text-sm text-muted-foreground">
                                                {questionSearchTerms[questionIndex] ? "No candidates found." : "No candidates available."}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              )}
                              
                              {question.assignedCandidates && question.assignedCandidates.length > 0 && (
                                <div className="text-sm text-green-600 flex items-center gap-1">
                                  <CheckCircle className="h-4 w-4" />
                                  Assigned to {question.assignedCandidates.length} candidate{question.assignedCandidates.length > 1 ? 's' : ''}
                                </div>
                              )}
                              
                              {(!question.assignedCandidates || question.assignedCandidates.length === 0) && (
                                <div className="text-sm text-orange-600 flex items-center gap-1">
                                  <AlertCircle className="h-4 w-4" />
                                  Not assigned to any candidates
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Add Another Question Button */}
                <div>
                  <Button
                    variant="outline"
                    onClick={addNewQuestion}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Another Question
                  </Button>
                </div>
            </div>
          </TabsContent>

          <TabsContent value="history" className="flex-1 flex flex-col min-h-0 mx-0 mt-0">
            {/* Fixed Header Section for History Tab */}
            <div className="flex-shrink-0 px-6 py-4 space-y-4 border-b bg-gray-50">
              {/* Search */}
              <div className="space-y-2">
                <Input
                  placeholder="Search questions by text, category, or tags..."
                  value={historySearchTerm}
                  onChange={(e) => setHistorySearchTerm(e.target.value)}
                />
              </div>

              {/* Candidate Assignment for History */}
              {eligibleCandidates.length > 0 && (
                <div className="bg-white p-4 rounded-lg border">
                  <Label className="text-sm font-medium mb-3 block">For whom are you assigning this question?</Label>
                  <Popover open={historyDropdownOpen} onOpenChange={setHistoryDropdownOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={historyDropdownOpen}
                        className="w-full justify-between min-h-10 h-auto py-2"
                      >
                        <div className="flex flex-wrap gap-1 max-w-[calc(100%-40px)] items-start">
                          <span className={cn(
                            "py-1 text-left",
                            selectedCandidates.length === 0 ? "text-muted-foreground" : ""
                          )}>
                            {selectedCandidates.length === 0
                              ? "Select candidates to assign questions to..."
                              : selectedCandidates.length === 1
                              ? eligibleCandidates.find(c => c._id === selectedCandidates[0])?.name
                              : `${selectedCandidates.length} candidates selected`
                            }
                          </span>
                        </div>
                        <div className="flex items-start gap-1 shrink-0 ml-2 mt-1">
                          {selectedCandidates.length > 0 && (
                            <X 
                              className="h-4 w-4 opacity-50 hover:opacity-100" 
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCandidates([]);
                              }}
                            />
                          )}
                          <ChevronsUpDown className="h-4 w-4 opacity-50" />
                        </div>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent 
                      className="w-full p-0" 
                      style={{ width: "max(var(--radix-popover-trigger-width), 320px)" }}
                      side="bottom"
                      align="start"
                      sideOffset={4}
                    >
                      <div className="relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
                        <div className="max-h-80 overflow-y-auto scrollbar-hidden">
                          <div className="p-1">
                            {/* Search Input */}
                            <div className="p-2 border-b mb-1 sticky top-0 bg-popover z-10">
                              <Input
                                placeholder="Search candidates..."
                                value={historyCandidateSearchTerm}
                                onChange={(e) => setHistoryCandidateSearchTerm(e.target.value)}
                                className="h-8"
                              />
                            </div>

                            {/* Individual Candidates */}
                            {eligibleCandidates
                              .filter(candidate => {
                                if (!historyCandidateSearchTerm) return true;
                                return candidate.name.toLowerCase().includes(historyCandidateSearchTerm.toLowerCase()) ||
                                       (candidate.email && candidate.email.toLowerCase().includes(historyCandidateSearchTerm.toLowerCase()));
                              })
                              .map((candidate) => (
                                <div
                                  key={candidate._id}
                                  onClick={() => {
                                    setSelectedCandidates(prev => 
                                      prev.includes(candidate._id)
                                        ? prev.filter(id => id !== candidate._id)
                                        : [...prev, candidate._id]
                                    );
                                  }}
                                  className="relative flex w-full cursor-default select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground cursor-pointer"
                                >
                                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                    <Check
                                      className={cn(
                                        "h-4 w-4",
                                        selectedCandidates.includes(candidate._id) ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                  </span>
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <User className="h-4 w-4 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium truncate" title={candidate.name}>
                                        {candidate.name}
                                      </div>
                                      {candidate.email && (
                                        <div className="text-xs text-muted-foreground truncate" title={candidate.email}>
                                          {candidate.email}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}

                            {/* No Results */}
                            {eligibleCandidates.filter(candidate => {
                              if (!historyCandidateSearchTerm) return true;
                              return candidate.name.toLowerCase().includes(historyCandidateSearchTerm.toLowerCase()) ||
                                     (candidate.email && candidate.email.toLowerCase().includes(historyCandidateSearchTerm.toLowerCase()));
                            }).length === 0 && (
                              <div className="p-4 text-center text-sm text-muted-foreground">
                                {historyCandidateSearchTerm ? "No candidates found." : "No candidates available."}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>

            {/* Scrollable History Questions List */}
            <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
              <div className="space-y-3">
                {historyLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                    Loading question history...
                  </div>
                ) : historyQuestions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {totalQuestions === 0 ? "No questions found in history" : "No questions match your search"}
                  </div>
                ) : (
                  historyQuestions.map((question) => (
                    <div
                      key={question._id}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedHistoryQuestions.includes(question._id)
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => {
                        setSelectedHistoryQuestions(prev =>
                          prev.includes(question._id)
                            ? prev.filter(id => id !== question._id)
                            : [...prev, question._id]
                        );
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Checkbox
                              checked={selectedHistoryQuestions.includes(question._id)}
                              readOnly
                            />
                            <Badge variant="outline" className="text-xs">
                              {question.difficulty}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {question.category}
                            </Badge>
                          </div>
                          <p className="font-medium mb-2">{question.question}</p>
                          <div className="text-sm text-gray-600 space-y-1">
                            {question.choices?.map((choice, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <span className="font-medium">
                                  {String.fromCharCode(65 + index)}.
                                </span>
                                <span className={question.answers?.includes(choice) ? "text-green-600 font-medium" : ""}>
                                  {choice}
                                  {question.answers?.includes(choice) && (
                                    <CheckCircle className="h-4 w-4 inline ml-1" />
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                          {question.tags && question.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {question.tags.map((tag, index) => (
                                <Badge key={index} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Compact Pagination Controls */}
              {!historyLoading && totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * questionsPerPage) + 1} to {Math.min(currentPage * questionsPerPage, totalQuestions)} of {totalQuestions} questions
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronUp className="h-4 w-4 rotate-[-90deg]" />
                    </Button>
                    
                    {/* Page Numbers - Show current and adjacent pages */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          className="h-8 w-8 p-0"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronUp className="h-4 w-4 rotate-90" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Fixed Footer - Outside of Tabs */}
        <div className="flex-shrink-0 px-6 py-4 border-t bg-white">
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            {activeTab === "create" ? (
              <Button 
                onClick={submitQuestions} 
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding Questions...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Add Questions ({questions.filter(q => q.assignedCandidates?.length > 0).length} assigned)
                  </>
                )}
              </Button>
            ) : (
              <Button 
                onClick={assignHistoryQuestions} 
                disabled={loading || selectedHistoryQuestions.length === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Assigning Questions...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Assign {selectedHistoryQuestions.length} Question{selectedHistoryQuestions.length > 1 ? 's' : ''}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ManualQuestionModal;